
import { motion } from 'framer-motion';
import { SeoHead } from '@/components/seo/SeoHead';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Mail, Phone, MapPin } from 'lucide-react';

const ContactPage = () => {
  const channels = [
    {
      icon: <Phone size={20} />,
      label: 'WhatsApp',
      value: '+55 (16) 99778-7674',
      href: 'https://wa.me/5516997787674?text=Olá%2C%20vim%20pelo%20site%20e%20quero%20saber%20mais.',
      cta: 'Falar agora →',
      primary: true,
    },
    {
      icon: <Mail size={20} />,
      label: 'E-mail',
      value: 'contato@johnnyvaz.com.br',
      href: 'mailto:contato@johnnyvaz.com.br',
      cta: 'Enviar e-mail',
      primary: false,
    },
    {
      icon: <MapPin size={20} />,
      label: 'Endereço',
      value: 'Av. Paulista, 1106 · Sala 01 · Andar 16\nSão Paulo/SP · CEP 01310-914\nCNPJ 63.548.781/0001-73',
      href: undefined,
      cta: undefined,
      primary: false,
    },
  ];

  return (
    <>
      <SeoHead
        route="/contact"
        lang="pt-BR"
        title="Contato — Johnny Vaz"
        description="Fale com Johnny Vaz sobre consultoria de IA, desenvolvimento de sistemas ou parceria. WhatsApp, e-mail ou agende diretamente."
      />
      <Navbar />

      {/* Hero */}
      <section style={{ backgroundColor: '#0B0B0B', paddingTop: '7rem', paddingBottom: '4rem' }}>
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FF4500', marginBottom: '1.25rem' }}>
              Contato · Johnny Vaz
            </p>
            <h1 style={{ fontFamily: "'Anton', 'Archivo Black', sans-serif", fontSize: 'clamp(3rem, 9vw, 6.5rem)', lineHeight: 0.88, letterSpacing: '-0.01em', textTransform: 'uppercase', color: '#F2EDE3', marginBottom: '2rem' }}>
              VAMOS<br />
              <span style={{ color: '#FF4500' }}>CONVERSAR</span>
            </h1>
            <div style={{ height: '2px', backgroundColor: '#FF4500', width: '64px', marginBottom: '2rem' }} />
            <p style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: 'clamp(1rem, 2.2vw, 1.2rem)', lineHeight: 1.65, color: '#8C8377', maxWidth: '480px' }}>
              Consultoria de IA, desenvolvimento de sistemas, parceria 50/50 ou só uma pergunta.
              Escolha o canal e fale agora.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Canais de contato */}
      <main>
        <section style={{ backgroundColor: '#F2EDE3', padding: '5rem 0' }}>
          <div className="container mx-auto px-6 max-w-4xl">
            <div className="grid md:grid-cols-3 gap-1">
              {channels.map((ch, i) => (
                <motion.div
                  key={ch.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  style={{
                    backgroundColor: ch.primary ? '#0B0B0B' : '#F2EDE3',
                    border: `1px solid ${ch.primary ? 'transparent' : 'rgba(11,11,11,0.12)'}`,
                    padding: '2.5rem',
                    display: 'flex',
                    flexDirection: 'column' as const,
                    gap: '1rem',
                  }}
                >
                  <div style={{ color: '#FF4500' }}>{ch.icon}</div>
                  <p style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: ch.primary ? '#8C8377' : '#8C8377' }}>
                    {ch.label}
                  </p>
                  <p style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: '0.95rem', lineHeight: 1.6, color: ch.primary ? '#F2EDE3' : '#0B0B0B', whiteSpace: 'pre-line' as const }}>
                    {ch.value}
                  </p>
                  {ch.href && ch.cta && (
                    <a
                      href={ch.href}
                      target={ch.href.startsWith('http') ? '_blank' : undefined}
                      rel={ch.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      style={{
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontSize: '0.7rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase' as const,
                        color: ch.primary ? '#0B0B0B' : '#0B0B0B',
                        backgroundColor: ch.primary ? '#FF4500' : 'transparent',
                        border: ch.primary ? 'none' : '1px solid rgba(11,11,11,0.3)',
                        padding: '0.6rem 1.25rem',
                        textDecoration: 'none',
                        display: 'inline-block',
                        marginTop: 'auto',
                        alignSelf: 'flex-start',
                      }}
                    >
                      {ch.cta}
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA agenda */}
        <section style={{ backgroundColor: '#0B0B0B', padding: '5rem 0' }}>
          <div className="container mx-auto px-6 max-w-3xl text-center">
            <p style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FF4500', marginBottom: '1.5rem' }}>
              Ou agende direto
            </p>
            <h2 style={{ fontFamily: "'Anton', 'Archivo Black', sans-serif", fontSize: 'clamp(2rem, 6vw, 3.5rem)', lineHeight: 0.92, letterSpacing: '-0.01em', textTransform: 'uppercase', color: '#F2EDE3', marginBottom: '1.5rem' }}>
              PREFERE ESCOLHER<br />
              <span style={{ color: '#FF4500' }}>DIA E HORA?</span>
            </h2>
            <p style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontSize: '1rem', lineHeight: 1.65, color: '#8C8377', marginBottom: '2rem' }}>
              Use o link abaixo para agendar uma conversa de 30 minutos diretamente no calendário.
            </p>
            <a
              href="https://calendar.app.google/11YBTxaW9hU3yJdJ8"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '0.85rem', fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', backgroundColor: '#FF4500', color: '#0B0B0B', padding: '1rem 2.5rem', textDecoration: 'none', display: 'inline-block' }}
            >
              Agendar conversa →
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default ContactPage;
